import { Component, ElementRef, Inject, OnInit, ViewChild, signal } from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AppValidators } from 'src/app/common/appValidators';
import { PolicyModel } from 'src/app/models/api/governance/policy.model';
import { BulkEndpointImportResponse } from 'src/app/models/api/governance/bulk-endpoint-import.model';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { PoliciesHttpService } from 'src/app/services/httpServices/governance/policiesHttpService/policies-http.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import * as _ from 'lodash';

@Component({
  selector: 'app-add-endpoint-dialog',
  templateUrl: './add-endpoint-dialog.component.html',
  styleUrls: ['./add-endpoint-dialog.component.scss'],
})
export class AddEndpointDialogComponent implements OnInit {
  @ViewChild('csvFileInput') csvFileInput!: ElementRef<HTMLInputElement>;

  public waitingForResponse = false;
  public addEndpointsFormGroup: UntypedFormGroup;
  public processingFile = signal(false);
  public skippedEndpoints = signal<string[]>([]);
  public invalidEndpoints = signal<string[]>([]);
  public showSkippedEndpoints = signal(false);
  public showInvalidEndpoints = signal(false);

  public readonly maxHostnamesToAdd = 500;

  constructor(
    public dialogRef: MatDialogRef<AddEndpointDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public policyData: PolicyModel,
    private readonly _formBuilder: UntypedFormBuilder,
    private readonly _policiesHttpService: PoliciesHttpService,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _notificationService: NotificationsService,
    private readonly _logger: LoggerService
  ) {}

  public get endpointsToAdd(): UntypedFormArray {
    return this.addEndpointsFormGroup.get('endpointsToAdd') as UntypedFormArray;
  }

  ngOnInit(): void {
    this.addEndpointsFormGroup = this._formBuilder.group({
      endpointsToAdd: this._formBuilder.array(
        [
          this._formBuilder.group({
            url: this._formBuilder.control('', [
              AppValidators.urlWithPathParams,
              Validators.required,
              this.createDuplicateValidator(),
            ]),
          }),
        ],
        [AppValidators.maxHostnames(this.maxHostnamesToAdd), AppValidators.uniqueEndpointFromPolicy(this.policyData)]
      ),
    });
  }

  private createDuplicateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value || !this.policyData?.config?.endpoints) {
        return null;
      }

      const endpoint = control.value.toLowerCase();
      const existingEndpoints = this.policyData.config.endpoints.map((e) => e.url.toLowerCase());

      if (existingEndpoints.includes(endpoint)) {
        const currentSkipped = this.skippedEndpoints();
        if (!currentSkipped.includes(endpoint)) {
          this.skippedEndpoints.set([...currentSkipped, endpoint]);
        }
        return { duplicate: { value: endpoint } };
      }

      const currentSkipped = this.skippedEndpoints();
      this.skippedEndpoints.set(currentSkipped.filter((e) => e !== endpoint));

      return null;
    };
  }

  public addMoreClicked() {
    const endpoints = this.endpointsToAdd;
    if (endpoints.length < this.maxHostnamesToAdd) {
      endpoints.push(
        this._formBuilder.group({
          url: this._formBuilder.control('', [
            AppValidators.urlWithPathParams,
            Validators.required,
            this.createDuplicateValidator(),
          ]),
        })
      );
    } else {
      this._notificationService.warning(`Maximum ${this.maxHostnamesToAdd} endpoints allowed`);
    }
  }

  public removeEndpoint(indexToRemove: number) {
    if (indexToRemove >= 0 && indexToRemove < this.endpointsToAdd.length) {
      const endpointControl = this.endpointsToAdd.at(indexToRemove);
      const endpointValue = endpointControl.get('url')?.value?.toLowerCase();

      this.endpointsToAdd.removeAt(indexToRemove);

      if (endpointValue) {
        const currentSkipped = this.skippedEndpoints();
        this.skippedEndpoints.set(currentSkipped.filter((e) => e.toLowerCase() !== endpointValue));
      }
    }
  }

  public addEndpoint() {
    const endpointsList = _.map(this.addEndpointsFormGroup.value.endpointsToAdd, 'url').filter(
      (url: string) => url && url.trim()
    );

    this.dialogRef.close({
      newEndpoints: endpointsList,
      skippedCount: this.skippedEndpoints().length,
      invalidCount: this.invalidEndpoints().length,
    });
  }

  public onClose(): void {
    this.dialogRef.close();
  }

  public toggleSkippedEndpoints(): void {
    this.showSkippedEndpoints.set(!this.showSkippedEndpoints());
  }

  public toggleInvalidEndpoints(): void {
    this.showInvalidEndpoints.set(!this.showInvalidEndpoints());
  }

  public triggerFileUpload(): void {
    this.csvFileInput.nativeElement.click();
  }

  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];

    if (!file.name.endsWith('.csv')) {
      this._notificationService.error('Please upload a CSV file');
      input.value = '';
      return;
    }

    this.processingFile.set(true);
    this.processCSVFile(file);
    input.value = '';
  }

  private processCSVFile(file: File): void {
    const integrationId = this.policyData.integrationId;
    const policyId = this.policyData.id;

    this._policiesHttpService.bulkImportEndpoints(integrationId, policyId, file).subscribe({
      next: (response: BulkEndpointImportResponse) => {
        this.processingFile.set(false);

        if (!response.success) {
          this._notificationService.error(response.message || 'Failed to process CSV file');
          return;
        }

        this.skippedEndpoints.set(response.skipped_endpoints);
        this.invalidEndpoints.set(response.invalid_endpoints);

        if (response.new_endpoints.length === 0) {
          if (response.skipped_count > 0 || response.invalid_endpoints.length > 0) {
            this._notificationService.info(response.message);
          } else {
            this._notificationService.warning('No endpoints found in the CSV file');
          }
          return;
        }

        const addedCount = this.addNewEndpointsToForm(response.new_endpoints);

        let message = `${addedCount} endpoint${addedCount > 1 ? 's' : ''} ready to add`;
        if (response.skipped_count > 0) {
          message += `, ${response.skipped_count} skipped`;
        }
        if (response.invalid_endpoints.length > 0) {
          message += `, ${response.invalid_endpoints.length} invalid`;
        }
        if (response.duplicates_removed > 0) {
          message += ` (${response.duplicates_removed} duplicate${response.duplicates_removed > 1 ? 's' : ''} removed from file)`;
        }

        this._notificationService.success(message);
      },
      error: (error) => {
        this.processingFile.set(false);
        this._logger.error('Error calling bulk import API', {}, error);
        this._httpErrorHandlerService.handle(error);
      },
    });
  }

  private addNewEndpointsToForm(endpoints: string[]): number {
    const formEndpoints = this.endpointsToAdd.controls
      .map((control) => control.get('url')?.value?.toLowerCase())
      .filter((url) => url);

    const formEndpointsSet = new Set(formEndpoints);

    const endpointsToAdd = endpoints.filter((endpoint) => !formEndpointsSet.has(endpoint.toLowerCase()));

    if (endpointsToAdd.length === 0) {
      return 0;
    }

    for (let i = this.endpointsToAdd.length - 1; i >= 0; i--) {
      const urlControl = this.endpointsToAdd.at(i).get('url');
      if (!urlControl?.value || urlControl.value.trim() === '') {
        this.endpointsToAdd.removeAt(i);
      }
    }

    let addedCount = 0;
    for (const endpoint of endpointsToAdd) {
      if (this.endpointsToAdd.length >= this.maxHostnamesToAdd) {
        this._notificationService.warning(
          `Maximum ${this.maxHostnamesToAdd} endpoints allowed. Only ${addedCount} endpoints were added.`
        );
        break;
      }

      this.endpointsToAdd.push(
        this._formBuilder.group({
          url: this._formBuilder.control(endpoint, [
            AppValidators.urlWithPathParams,
            Validators.required,
            this.createDuplicateValidator(),
          ]),
        })
      );
      addedCount++;
    }

    return addedCount;
  }
}
